(function () {
  'use strict';

  const TEMPLATE_SELECTOR = '[data-mega-menu-template]';
  const DEFAULT_SECTION = 'mega-menu3';
  const MAX_RETRIES = 3;

  const globalScope = window.MomcozyHeaderMegaMenu || (window.MomcozyHeaderMegaMenu = {});
  if (globalScope.loader) return;

  const loaderState = {
    loaded: false,
    loading: false,
    retryCount: 0,
    config: null,
  };

  // 主题编辑器（设计模式）下 section 已由 Liquid 直接渲染，不需要懒加载，
  // 且需要额外处理 shopify:block:select 事件来联动展开面板。
  const isThemeEditor = () => Boolean(window.Shopify && window.Shopify.designMode);

  // 编辑器里 section 重新渲染会重建 DOM，记录最近选中的 block 以便恢复展开状态。
  let themeEditorSelectedBlockId = '';

  const readHeaderOptions = (root) => {
    try {
      return JSON.parse(root?.dataset?.headerOptions || '{}');
    } catch (error) {
      console.warn('[header mega menu] invalid data-header-options', error);
      return {};
    }
  };

  const readNumberOption = (options, key, fallback) => {
    const value = Number(options?.[key]);
    return Number.isFinite(value) ? value : fallback;
  };

  const readMegaMenuConfig = (root) => {
    const header = root?.closest('header.shopify-section');
    const options = readHeaderOptions(root);
    return {
      headerElementId: header?.id || '',
      megaMenu3Section: options.megaMenu3Section || DEFAULT_SECTION,
      routesRoot: options.routesRoot || '',
      showLogoLabel: Boolean(options.megaMenuShowLogoLabel),
      stickyHeader: Boolean(options.megaMenuStickyHeader),
      scrollHeader: Boolean(options.megaMenuScrollHeader),
      scrollHideThreshold: readNumberOption(options, 'megaMenuScrollHideThreshold', 400),
    };
  };

  const getRoutesRoot = () => {
    const configuredRoot = loaderState.config?.routesRoot;
    if (configuredRoot) return configuredRoot;
    return window.Shopify?.routes?.root || '/';
  };

  const buildSectionsUrl = () => {
    const root = getRoutesRoot();
    const sectionName = loaderState.config?.megaMenu3Section || DEFAULT_SECTION;
    return `${root}?sections=${encodeURIComponent(sectionName)}`;
  };

  const dispatchReady = () => {
    document.dispatchEvent(new CustomEvent('mega-menu3:ready'));
  };

  const hasTemplates = () => document.querySelector(TEMPLATE_SELECTOR) !== null;

  const insertSection = (html) => {
    if (!html) return false;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const sectionEl = wrapper.firstElementChild;
    if (!sectionEl) return false;

    const headerEl = document.getElementById(loaderState.config?.headerElementId || '');
    if (headerEl?.parentNode) {
      headerEl.parentNode.insertBefore(sectionEl, headerEl.nextSibling);
    } else {
      document.body.appendChild(sectionEl);
    }

    return true;
  };

  const resetForRetry = () => {
    loaderState.loaded = false;
    loaderState.loading = false;
    loaderState.retryCount = 0;
  };

  const requestTemplates = () => {
    if (loaderState.loaded || loaderState.loading) return;
    // 设计模式下 header 与 mega-menu3 都由 Liquid 渲染，避免重复请求插入
    if (isThemeEditor()) return;

    if (hasTemplates()) {
      loaderState.loaded = true;
      dispatchReady();
      return;
    }

    loaderState.loading = true;
    fetch(buildSectionsUrl(), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const sectionName = loaderState.config?.megaMenu3Section || DEFAULT_SECTION;
        const inserted = insertSection(data?.[sectionName]);
        loaderState.loading = false;
        loaderState.loaded = inserted || hasTemplates();
        if (loaderState.loaded) {
          dispatchReady();
        }
      })
      .catch((error) => {
        loaderState.loading = false;
        if (loaderState.retryCount < MAX_RETRIES) {
          const delay = Math.pow(2, loaderState.retryCount) * 1000;
          loaderState.retryCount += 1;
          window.setTimeout(requestTemplates, delay);
          return;
        }

        console.warn('[mega-menu3 lazy]', error);
        resetForRetry();
      });
  };

  const scheduleIdleLoad = () => {
    if (isThemeEditor()) return;
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(requestTemplates, { timeout: 800 });
    } else {
      window.setTimeout(requestTemplates, 800);
    }
  };

  const getMenuRoots = () => [...document.querySelectorAll('momcozy-mega-menu, [data-mega-menu]')];

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const isVisible = (element) => !!(element && element.offsetParent);

  const setupLogoResize = (root) => {
    if (!loaderState.config?.showLogoLabel) return;
    if (!root || root.dataset.logoResizeEnhanced === 'true') return;
    root.dataset.logoResizeEnhanced = 'true';

    const getElement = (selector) => root.querySelector(selector);
    const nav = getElement('.mega-menu__nav');
    const logo = getElement('.mega-menu__nav-logo');
    const buttons = getElement('.mega-menu__nav-btns');
    if (!nav || !logo || !buttons) return;

    const logoSelectors = [
      '.momcozy-logo-dark--mobile--svg',
      '.momcozy-logo-dark--pc--svg',
      '.momcozy-logo-light--mobile--svg',
      '.momcozy-logo-light--pc--svg',
      'picture.momcozy-logo-dark',
      'picture.momcozy-logo-light',
    ];
    if (!root.querySelector(logoSelectors.join(','))) return;

    const getActiveLogoElement = () => {
      for (const selector of logoSelectors) {
        const element = getElement(selector);
        if (isVisible(element)) return element;
      }
      return null;
    };

    const minScale = 0.6;
    const gap = 8;
    let rafId = 0;

    const calculate = () => {
      rafId = 0;

      if (window.innerWidth > 768) {
        root.style.setProperty('--mm-logo-scale', '1');
        return;
      }

      const activeLogo = getActiveLogoElement();
      if (!activeLogo || !isVisible(buttons)) {
        root.style.setProperty('--mm-logo-scale', '1');
        return;
      }

      root.style.setProperty('--mm-logo-scale', '1');

      const buttonsRect = buttons.getBoundingClientRect();
      const limitX = (buttonsRect.left || 0) - gap;
      const isShowLabel = logo.classList.contains('show-logo-label');
      const scaleTarget = isShowLabel ? logo : activeLogo;
      const targetRect = scaleTarget.getBoundingClientRect();
      if (!targetRect.width) return;

      const isOverlapping = (targetRect.right || 0) + gap > (buttonsRect.left || 0);
      if (!isOverlapping) {
        root.style.setProperty('--mm-logo-scale', '1');
        return;
      }

      let desiredScale = 1;
      if (isShowLabel) {
        desiredScale = (limitX - (targetRect.left || 0)) / (targetRect.width || 1);
      } else {
        const centerX = (targetRect.left || 0) + (targetRect.width || 0) / 2;
        desiredScale = (2 * (limitX - centerX)) / (targetRect.width || 1);
      }

      desiredScale = clamp(desiredScale, minScale, 1);
      root.style.setProperty('--mm-logo-scale', String(desiredScale));

      let safeScale = desiredScale;
      for (let index = 0; index < 6; index += 1) {
        const currentRect = scaleTarget.getBoundingClientRect();
        const currentButtonsRect = buttons.getBoundingClientRect();
        if ((currentRect.right || 0) + gap <= (currentButtonsRect.left || 0)) break;
        if (safeScale <= minScale) break;
        safeScale = clamp(safeScale * 0.96, minScale, safeScale);
        root.style.setProperty('--mm-logo-scale', String(safeScale));
      }
    };

    const schedule = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(calculate);
    };

    schedule();

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(buttons);
    }

    if (document.fonts?.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(schedule);
    }

    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
    window.addEventListener('load', schedule, { once: true, passive: true });
  };

  const setupHeaderChrome = () => {
    if (loaderState.config?.chromeEnhanced) return;
    loaderState.config.chromeEnhanced = true;

    const header =
      document.getElementById(loaderState.config?.headerElementId || '') ||
      document.querySelector('.t4s-section-header.header-e-filter');

    if (!header) return;

    const heightProperty = '--header-height2';
    document.documentElement.style.setProperty(heightProperty, header.offsetHeight + 'px');

    if (!loaderState.config?.stickyHeader) return;

    header.classList.add('shopify-section-header-sticky');
    document.documentElement.classList.toggle('mega-menu-sticky-always', !loaderState.config?.scrollHeader);
    const hideOnScroll = Boolean(loaderState.config?.scrollHeader);
    const threshold = Number(loaderState.config?.scrollHideThreshold ?? 400);
    const html = document.documentElement;
    let hiddenOn = false;
    let lastY = window.pageYOffset || document.documentElement.scrollTop;
    let stuckOn = false;
    let ticking = false;

    const hide = () => {
      if (!hideOnScroll || hiddenOn) return;
      hiddenOn = true;
      header.classList.add('shopify-section-header-hidden');
      header.dispatchEvent(new Event('T4sHeaderHide'));
    };

    const show = () => {
      if (!hiddenOn) return;
      hiddenOn = false;
      header.classList.remove('shopify-section-header-hidden');
      header.dispatchEvent(new Event('T4sHeaderReveal'));
    };

    const setStuck = (on) => {
      if (stuckOn === on) return;
      stuckOn = on;
      html.classList.toggle('is-header--stuck', on);
    };

    const update = () => {
      document.documentElement.style.setProperty(heightProperty, header.offsetHeight + 'px');
      const y = window.pageYOffset || document.documentElement.scrollTop;

      if (y < 1) {
        header.classList.add('shopify-section-header-sticky');
        setStuck(false);
        show();
        lastY = y;
        return;
      }

      if (y < threshold) {
        setStuck(false);
        show();
        lastY = y;
        return;
      }

      setStuck(true);

      if (y > lastY) {
        hide();
      } else if (y < lastY) {
        show();
      }
      lastY = y;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  };

  const setupDesktopMenu = (section) => {
    const menuSvg = {
      viewMore: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 48 48">
          <path fill="#000" fill-rule="evenodd" d="M24.804 15.503a1.39 1.39 0 0 1 1.967 0l8.552 8.553-8.804 8.803a1.392 1.392 0 1 1-1.967-1.966l5.501-5.501h-16.22l-.141-.007a1.4 1.4 0 0 1-.263-.054l-.047-.017-.084-.03-.028-.013-.093-.045-.024-.014-.083-.05-.04-.028-.071-.053-.026-.021-.069-.064-.032-.031-.06-.067-.026-.032-.049-.065-.028-.04q-.022-.034-.04-.067l-.03-.053-.022-.048-.035-.076-.012-.034q-.016-.043-.03-.088a1.392 1.392 0 0 1 1.333-1.786h16.109l-5.138-5.138a1.39 1.39 0 0 1 0-1.968" clip-rule="evenodd"/>
          <path fill="#000" fill-rule="evenodd" d="M24 0c13.255 0 24 10.745 24 24S37.255 48 24 48 0 37.255 0 24 10.745 0 24 0m0 2.713C12.244 2.713 2.713 12.243 2.713 24S12.243 45.287 24 45.287 45.287 35.757 45.287 24 35.757 2.713 24 2.713" clip-rule="evenodd"/>
      </svg>
      `,
    };

    if (!section || section.dataset.enhanced === 'true') return;
    section.dataset.enhanced = 'true';

    const forceOpen = section.dataset.forceOpen === 'true';
    const navButtons = [...section.querySelectorAll('[data-menu-trigger]')];
    const directLinkButtons = [...section.querySelectorAll('[data-direct-link="true"]')];
    const panels = [...section.querySelectorAll('[data-menu-panel]')];
    const panelContainer = section.querySelector('[data-menu-panels]');
    const navElement = section.querySelector('.mega-menu__nav');
    const navList = section.querySelector('.nav-list');
    const primaryNavList = section.querySelector('.mega-menu__nav-list--primary');
    const navArrowGroup = section.querySelector('.mega-menu__arrow-group');
    const navScrollPrev = navArrowGroup?.querySelector('[data-nav-scroll-prev]');
    const navScrollNext = navArrowGroup?.querySelector('[data-nav-scroll-next]');
    const overlayId = `${section.id}-overlay`;
    let overlay = null;
    let closeTimer = null;
    let overlayReflowHandler = null;
    let activePanel = null;
    let panelHeightRaf = null;
    let panelHeightMeasureRaf = null;
    let panelHeightTimer = null;
    let panelHeightTransitionHandler = null;
    let panelHeightToken = 0;
    let panelResizeObserver = null;
    let observedPanelHeight = 0;
    let isPanelClosing = false;
    let isPanelHeightAnimating = false;
    let openingPanelId = null;
    let openingPanelSwitched = false;
    let subItemEntryTimer = null;
    let cardEntryTimer = null;
    let cardEntryRaf = null;

    const PANEL_HEIGHT_DURATION = 300;
    const PANEL_MAX_HEIGHT_RATIO = 0.8;
    const ITEM_ENTRY_DURATION = 600;
    const ITEM_STAGGER_DELAY = 60;
    const ITEM_STAGGER_MAX_INDEX = 7;
    const CARD_ENTRY_GROUP_DELAY = 60;
    const panelMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const clearSubItemEntryAnimation = () => {
      if (subItemEntryTimer) {
        window.clearTimeout(subItemEntryTimer);
        subItemEntryTimer = null;
      }
      panels.forEach((panel) => {
        panel.classList.remove('is-subitems-entering');
        panel.querySelectorAll('.mega-menu__sub-item, .mega-menu__panel-button').forEach((item) => {
          item.style.removeProperty('--mmpc-item-delay');
        });
      });
    };

    const clearCardEntryAnimation = () => {
      if (cardEntryRaf) {
        window.cancelAnimationFrame(cardEntryRaf);
        cardEntryRaf = null;
      }
      if (cardEntryTimer) {
        window.clearTimeout(cardEntryTimer);
        cardEntryTimer = null;
      }
      panels.forEach((panel) => {
        panel.classList.remove('is-cards-preparing');
        panel.classList.remove('is-cards-entering');
        panel
          .querySelectorAll('.mega-menu3__card, .mega-menu3__view-more, .mega-menu__panel-banner-item')
          .forEach((item) => {
            item.style.removeProperty('--mmpc-item-delay');
            item.style.removeProperty('animation-delay');
          });
      });
    };

    const clearItemEntryAnimations = () => {
      clearSubItemEntryAnimation();
      clearCardEntryAnimation();
    };

    const beginOpeningEntryCycle = (panelId) => {
      clearItemEntryAnimations();
      openingPanelId = panelId;
      openingPanelSwitched = false;
    };

    const cancelOpeningEntryCycle = () => {
      openingPanelSwitched = true;
      openingPanelId = null;
      clearItemEntryAnimations();
    };

    const canPlayOpeningEntry = (panel) => {
      return (
        !panelMotionQuery.matches &&
        !openingPanelSwitched &&
        openingPanelId === panel?.dataset.menuPanel &&
        activePanel === panel &&
        panelContainer?.dataset.open === 'true' &&
        !isPanelClosing
      );
    };

    const playSubItemEntryAnimation = (panel) => {
      if (!canPlayOpeningEntry(panel)) return;
      const items = [...panel.querySelectorAll('.mega-menu__sub-item, .mega-menu__panel-button')];
      if (items.length === 0) return;

      clearSubItemEntryAnimation();
      items.forEach((item, index) => {
        const delay = Math.min(index, ITEM_STAGGER_MAX_INDEX) * ITEM_STAGGER_DELAY;
        item.style.setProperty('--mmpc-item-delay', `${delay}ms`);
      });
      panel.classList.add('is-subitems-entering');
      const maxDelay = Math.min(items.length - 1, ITEM_STAGGER_MAX_INDEX) * ITEM_STAGGER_DELAY;
      subItemEntryTimer = window.setTimeout(clearSubItemEntryAnimation, ITEM_ENTRY_DURATION + maxDelay + 50);
    };

    const playCardEntryAnimation = (panel) => {
      if (!canPlayOpeningEntry(panel)) return false;
      const items = [
        ...panel.querySelectorAll(
          '.mega-menu3__card:not([hidden]), .mega-menu3__view-more, .mega-menu__panel-banner-item'
        ),
      ];
      if (items.length === 0) return false;

      clearCardEntryAnimation();
      items.forEach((item, index) => {
        const delay = CARD_ENTRY_GROUP_DELAY + Math.min(index, ITEM_STAGGER_MAX_INDEX) * ITEM_STAGGER_DELAY;
        item.style.setProperty('--mmpc-item-delay', `${delay}ms`);
        item.style.setProperty('animation-delay', `${delay}ms`);
      });
      const maxDelay = CARD_ENTRY_GROUP_DELAY + Math.min(items.length - 1, ITEM_STAGGER_MAX_INDEX) * ITEM_STAGGER_DELAY;
      panel.classList.add('is-cards-preparing');
      cardEntryRaf = window.requestAnimationFrame(() => {
        cardEntryRaf = null;
        if (!canPlayOpeningEntry(panel)) {
          clearCardEntryAnimation();
          return;
        }
        panel.classList.remove('is-cards-preparing');
        panel.classList.add('is-cards-entering');
        cardEntryTimer = window.setTimeout(clearCardEntryAnimation, ITEM_ENTRY_DURATION + maxDelay + 50);
      });
      return true;
    };

    const clearPanelHeightAnimation = () => {
      panelHeightToken += 1;
      isPanelHeightAnimating = false;
      if (panelHeightRaf) {
        window.cancelAnimationFrame(panelHeightRaf);
        panelHeightRaf = null;
      }
      if (panelHeightTimer) {
        window.clearTimeout(panelHeightTimer);
        panelHeightTimer = null;
      }
      if (panelContainer && panelHeightTransitionHandler) {
        panelContainer.removeEventListener('transitionend', panelHeightTransitionHandler);
        panelHeightTransitionHandler = null;
      }
    };

    const freezePanelHeight = (height) => {
      if (!panelContainer) return;
      clearPanelHeightAnimation();
      const currentHeight = height ?? panelContainer.getBoundingClientRect().height;
      panelContainer.style.transition = 'none';
      panelContainer.style.height = `${Math.max(currentHeight, 0)}px`;
      void panelContainer.offsetHeight;
    };

    const measureNaturalPanelHeight = () => {
      if (!panelContainer) return 0;
      const previousHeight = panelContainer.style.height;
      const previousTransition = panelContainer.style.transition;
      panelContainer.style.transition = 'none';
      panelContainer.style.height = 'auto';
      const naturalHeight = Math.min(panelContainer.scrollHeight, window.innerHeight * PANEL_MAX_HEIGHT_RATIO);
      panelContainer.style.height = previousHeight;
      panelContainer.style.transition = previousTransition;
      return naturalHeight;
    };

    const animatePanelHeight = (targetHeight, onComplete) => {
      if (!panelContainer) {
        onComplete?.();
        return;
      }

      clearPanelHeightAnimation();
      const token = panelHeightToken;
      const currentHeight = panelContainer.getBoundingClientRect().height;
      observedPanelHeight = targetHeight;
      isPanelHeightAnimating = true;

      const finish = () => {
        if (token !== panelHeightToken) return;
        if (!isPanelClosing) {
          const latestHeight = measureNaturalPanelHeight();
          if (Math.abs(latestHeight - targetHeight) >= 1) {
            freezePanelHeight(targetHeight);
            animatePanelHeight(latestHeight, onComplete);
            return;
          }
        }
        clearPanelHeightAnimation();
        panelContainer.style.transition = '';
        panelContainer.style.height = isPanelClosing ? '0px' : 'auto';
        onComplete?.();
      };

      if (panelMotionQuery.matches || Math.abs(currentHeight - targetHeight) < 1) {
        panelContainer.style.height = `${targetHeight}px`;
        finish();
        return;
      }

      panelContainer.style.transition = `height ${PANEL_HEIGHT_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      panelHeightTransitionHandler = (event) => {
        if (event.target !== panelContainer || event.propertyName !== 'height') return;
        finish();
      };
      panelContainer.addEventListener('transitionend', panelHeightTransitionHandler);
      panelHeightRaf = window.requestAnimationFrame(() => {
        panelHeightRaf = null;
        if (token !== panelHeightToken) return;
        panelContainer.style.height = `${Math.max(targetHeight, 0)}px`;
      });
      panelHeightTimer = window.setTimeout(finish, PANEL_HEIGHT_DURATION + 80);
    };

    const scheduleNaturalPanelHeight = () => {
      if (!panelContainer || panelContainer.dataset.open !== 'true' || isPanelClosing) return;
      if (panelHeightMeasureRaf) window.cancelAnimationFrame(panelHeightMeasureRaf);
      panelHeightMeasureRaf = window.requestAnimationFrame(() => {
        panelHeightMeasureRaf = null;
        if (!panelContainer || panelContainer.dataset.open !== 'true' || isPanelClosing) return;
        animatePanelHeight(measureNaturalPanelHeight());
      });
    };

    const observeActivePanel = (panel) => {
      if (!('ResizeObserver' in window)) return;
      if (!panelResizeObserver) {
        panelResizeObserver = new ResizeObserver(() => {
          if (!panelContainer || panelContainer.dataset.open !== 'true' || isPanelClosing || isPanelHeightAnimating)
            return;
          const targetHeight = measureNaturalPanelHeight();
          if (Math.abs(targetHeight - observedPanelHeight) < 1) return;
          const currentHeight =
            panelContainer.style.height === 'auto' || panelContainer.style.height === ''
              ? observedPanelHeight
              : panelContainer.getBoundingClientRect().height;
          freezePanelHeight(currentHeight);
          animatePanelHeight(targetHeight);
        });
      }
      panelResizeObserver.disconnect();
      if (!panel) return;
      const observedElements = panel.querySelectorAll(
        '.mega-menu__panel-left, .mega-menu__panel-content, .mega-menu__panel-banner'
      );
      observedElements.forEach((element) => panelResizeObserver.observe(element));
    };

    const activateHoverState = () => {
      section.classList.add('mega-menu-filter--hover');
    };

    const deactivateHoverState = () => {
      section.classList.remove('mega-menu-filter--hover');
    };

    const initPrimaryNavScroll = () => {
      if (!primaryNavList || !navArrowGroup || !navScrollPrev || !navScrollNext) return;

      let navScrollRaf = null;

      const syncNavScrollState = () => {
        navScrollRaf = null;
        const maxScrollLeft = primaryNavList.scrollWidth - primaryNavList.clientWidth;
        const isOverflowing = primaryNavList.scrollWidth > primaryNavList.clientWidth + 1;
        const isAtStart = primaryNavList.scrollLeft <= 1;
        const isAtEnd = primaryNavList.scrollLeft >= maxScrollLeft - 1;

        navArrowGroup.classList.toggle('is-overflowing', isOverflowing);
        navScrollPrev.disabled = !isOverflowing || isAtStart;
        navScrollNext.disabled = !isOverflowing || isAtEnd;
      };

      const scheduleNavScrollSync = () => {
        if (navScrollRaf) return;
        navScrollRaf = window.requestAnimationFrame(syncNavScrollState);
      };

      const scrollPrimaryNav = (direction) => {
        const distance = Math.max(primaryNavList.clientWidth * 0.8, 160);
        primaryNavList.scrollBy({ left: direction * distance, behavior: 'smooth' });
      };

      navScrollPrev.addEventListener('click', () => scrollPrimaryNav(-1));
      navScrollNext.addEventListener('click', () => scrollPrimaryNav(1));
      primaryNavList.addEventListener('scroll', scheduleNavScrollSync, { passive: true });
      window.addEventListener('resize', scheduleNavScrollSync, { passive: true });

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(scheduleNavScrollSync);
        resizeObserver.observe(primaryNavList);
      }

      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(scheduleNavScrollSync);
      }

      scheduleNavScrollSync();
    };

    initPrimaryNavScroll();

    const updateOverlayPosition = () => {
      if (!overlay || !navElement) return;
      const rect = navElement.getBoundingClientRect();
      const top = Math.max(rect.bottom, 0);
      overlay.style.top = `${top}px`;
      overlay.style.left = '0';
      overlay.style.right = '0';
      overlay.style.bottom = '0';
      overlay.style.height = `calc(${window.innerHeight}px - ${top}px)`;
    };

    if (!forceOpen) {
      overlay = document.getElementById(overlayId);
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = overlayId;
        overlay.className = 'mega-menu__overlay';
        overlay.dataset.active = 'false';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        // 将 overlay 插入到 section 之前，而不是 body
        if (section.parentNode) {
          section.parentNode.insertBefore(overlay, section);
        } else {
          document.body.appendChild(overlay);
        }
      }
      if (overlay && !overlayReflowHandler) {
        overlayReflowHandler = () => {
          if (!overlay || overlay.dataset.active !== 'true') return;
          updateOverlayPosition();
        };
        window.addEventListener('resize', overlayReflowHandler);
        window.addEventListener('scroll', overlayReflowHandler, true);
      }
    }

    const cancelClose = () => {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
    };

    // 判断鼠标是否在"热区"内（nav-list 或 panels）
    const isMouseInHotzone = (x, y) => {
      // 检查 nav-list 区域（第二行菜单列表）
      const navListRect = navList ? navList.getBoundingClientRect() : null;
      if (
        navListRect &&
        x >= navListRect.left &&
        x <= navListRect.right &&
        y >= navListRect.top &&
        y <= navListRect.bottom
      ) {
        return true;
      }
      // 检查 panels 区域
      if (panelContainer && panelContainer.dataset.open === 'true') {
        const panelRect = panelContainer.getBoundingClientRect();
        if (x >= panelRect.left && x <= panelRect.right && y >= panelRect.top && y <= panelRect.bottom) {
          return true;
        }
      }
      return false;
    };

    let mouseMoveHandler = null;

    const startMouseTracking = () => {
      if (mouseMoveHandler) return;
      mouseMoveHandler = (e) => {
        if (!isMouseInHotzone(e.clientX, e.clientY)) {
          scheduleClose();
        } else {
          cancelClose();
        }
      };
      document.addEventListener('mousemove', mouseMoveHandler);
    };

    const stopMouseTracking = () => {
      if (mouseMoveHandler) {
        document.removeEventListener('mousemove', mouseMoveHandler);
        mouseMoveHandler = null;
      }
    };

    const scheduleClose = () => {
      if (forceOpen) return;
      cancelClose();
      closeTimer = window.setTimeout(() => {
        closeAll();
        stopMouseTracking();
        closeTimer = null;
      }, 100);
    };

    // 检查元素是否可滚动以及是否在滚动边界
    const isScrollable = (element) => {
      if (!element) return false;
      const hasScrollableContent = element.scrollHeight > element.clientHeight;
      return hasScrollableContent;
    };

    const isAtScrollBoundary = (element, deltaY) => {
      if (!element) return true;
      const { scrollTop, scrollHeight, clientHeight } = element;

      // 向上滚动且已到顶部
      if (deltaY < 0 && scrollTop <= 0) {
        return true;
      }

      // 向下滚动且已到底部
      if (deltaY > 0 && scrollTop + clientHeight >= scrollHeight) {
        return true;
      }

      return false;
    };

    // 阻止 body 滚动的处理函数（但允许标记区域内部滚动）
    const preventScroll = (e) => {
      // 查找最近的可滚动父元素
      const scrollableArea = e.target instanceof Element ? e.target.closest('.scrollable-area') : null;
      const scrollableParent =
        panelContainer?.contains(scrollableArea) && isScrollable(scrollableArea) ? scrollableArea : null;

      // 如果找到可滚动的父元素
      if (scrollableParent) {
        const deltaY =
          e.deltaY ||
          (e.touches && e.touches.length > 0
            ? e.touches[0].clientY - (e.touches[0]._startY || e.touches[0].clientY)
            : 0);

        // 检查是否在滚动边界，如果在边界则阻止默认行为
        if (isAtScrollBoundary(scrollableParent, deltaY)) {
          e.preventDefault();
          e.stopPropagation();
        } else {
          // 允许容器内滚动，但阻止冒泡到 body
          e.stopPropagation();
        }
      } else {
        // 不在可滚动容器内，阻止滚动
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 锁定滚动（保持滚动条可见）
    const lockScroll = () => {
      // 阻止鼠标滚轮滚动
      window.addEventListener('wheel', preventScroll, { passive: false });
      // 阻止键盘滚动
      window.addEventListener('keydown', preventScrollKeys, { passive: false });
    };

    // 解锁滚动
    const unlockScroll = () => {
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('keydown', preventScrollKeys);
    };

    // 阻止键盘滚动（空格、方向键、Page Up/Down等）
    const preventScrollKeys = (e) => {
      const keys = [32, 33, 34, 35, 36, 37, 38, 39, 40]; // space, page up, page down, end, home, arrows
      if (keys.includes(e.keyCode)) {
        // 检查焦点是否在 panel 内部
        const isInsidePanel = document.activeElement?.closest('.scrollable-area');
        if (!isInsidePanel) {
          e.preventDefault();
          return false;
        }
      }
    };

    const closeAll = () => {
      cancelClose();
      stopMouseTracking();
      cancelOpeningEntryCycle();
      if (!forceOpen) {
        deactivateHoverState();
      }
      if (forceOpen) return;
      navButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (overlay) overlay.dataset.active = 'false';
      if (!panelContainer || panelContainer.dataset.open !== 'true') return;

      isPanelClosing = true;
      panelResizeObserver?.disconnect();
      panelContainer.classList.remove('is-entering');
      panelContainer.classList.add('is-closing');
      freezePanelHeight();
      animatePanelHeight(0, () => {
        if (!isPanelClosing) return;
        panels.forEach((panel) => (panel.hidden = true));
        panelContainer.dataset.open = 'false';
        panelContainer.classList.remove('is-closing');
        panelContainer.style.height = '';
        activePanel = null;
        observedPanelHeight = 0;
        isPanelClosing = false;
        const websiteWrapper = document.querySelector('.t4s-website-wrapper');
        const headerSection = section.closest('header.shopify-section');
        if (websiteWrapper) websiteWrapper.classList.remove('panel-is-open');
        if (headerSection) headerSection.classList.remove('panel-is-open');
        unlockScroll();
      });
    };

    const openPanel = (panelId) => {
      cancelClose();
      const targetPanel = panels.find((panel) => panel.dataset.menuPanel === panelId);
      if (!targetPanel) return;
      const wasOpen = panelContainer?.dataset.open === 'true';
      const startsOpeningCycle = !wasOpen || isPanelClosing;
      if (startsOpeningCycle) {
        beginOpeningEntryCycle(panelId);
      } else if (openingPanelId && openingPanelId !== panelId) {
        cancelOpeningEntryCycle();
      }
      const startHeight = wasOpen && panelContainer ? panelContainer.getBoundingClientRect().height : 0;
      isPanelClosing = false;
      panelContainer?.classList.remove('is-closing');
      freezePanelHeight(startHeight);
      panels.forEach((panel) => (panel.hidden = panel !== targetPanel));
      navButtons.forEach((button) =>
        button.setAttribute('aria-expanded', button.dataset.menuTrigger === panelId ? 'true' : 'false')
      );
      if (panelContainer) {
        panelContainer.dataset.open = 'true';
        // 只在从关闭到打开时播放进场动画
        if (!wasOpen) {
          panelContainer.classList.remove('is-entering');
          void panelContainer.offsetWidth; // 强制 reflow
          panelContainer.classList.add('is-entering');
          const onEnd = (e) => {
            if (e.target !== panelContainer) return; // 忽略子元素动画冒泡
            panelContainer.removeEventListener('animationend', onEnd);
            panelContainer.classList.remove('is-entering');
          };
          panelContainer.addEventListener('animationend', onEnd);
        }
      }
      // 编辑器预览中不做鼠标跟踪，避免鼠标移出后 100ms 自动收起面板
      if (!isThemeEditor()) startMouseTracking();
      if (!forceOpen && overlay) {
        overlay.dataset.active = 'true';
        updateOverlayPosition();
      } else if (overlay) {
        overlay.dataset.active = 'false';
      }
      activePanel = targetPanel;
      activateDefaultSub(targetPanel);
      if (startsOpeningCycle) {
        playSubItemEntryAnimation(targetPanel);
        playCardEntryAnimation(targetPanel);
      }
      observeActivePanel(targetPanel);
      scheduleNaturalPanelHeight();
      activateHoverState();
      // 为兼容性，给父元素添加类名（隐藏 sidebar 按钮、移除 transform）
      const websiteWrapper = document.querySelector('.t4s-website-wrapper');
      const headerSection = section.closest('header.shopify-section');
      if (websiteWrapper) websiteWrapper.classList.add('panel-is-open');
      if (headerSection) headerSection.classList.add('panel-is-open');
      // 锁定滚动（编辑器预览下不锁定，方便配置时浏览页面）
      if (!isThemeEditor()) lockScroll();
    };

    const activateDefaultSub = (panel) => {
      const subTriggers = [...panel.querySelectorAll('[data-sub-trigger]')];
      if (subTriggers.length === 0) {
        updatePanelContent(panel, panel.dataset.directTemplate, null);
        return;
      }
      const activeTrigger =
        subTriggers.find((trigger) => trigger.getAttribute('aria-current') === 'true') || subTriggers[0];
      activateSubPanel(panel, activeTrigger.dataset.subTrigger, activeTrigger.dataset.subTemplate, activeTrigger);
    };

    const activateSubPanel = (panel, subId, templateHandle, triggerElement) => {
      const subTriggers = [...panel.querySelectorAll('[data-sub-trigger]')];
      subTriggers.forEach((trigger) =>
        trigger.setAttribute('aria-current', trigger.dataset.subTrigger === subId ? 'true' : 'false')
      );
      updatePanelContent(panel, templateHandle, triggerElement);
    };

    const appendRefToAnchor = (anchor, refValue) => {
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const originalHref = anchor.getAttribute('href');
      if (!originalHref) return;
      try {
        const url = new URL(originalHref, window.location.origin);
        url.searchParams.set('ref', refValue);
        const isAbsolute = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(originalHref);
        if (isAbsolute) {
          anchor.setAttribute('href', url.toString());
        } else {
          const relativeHref = `${url.pathname}${url.search}${url.hash}`;
          anchor.setAttribute('href', relativeHref);
        }
      } catch (error) {
        if (!originalHref.includes('javascript:') && !originalHref.startsWith('#')) {
          const separator = originalHref.includes('?') ? '&' : '?';
          anchor.setAttribute('href', `${originalHref}${separator}ref=${encodeURIComponent(refValue)}`);
        }
      }
    };

    const getDiscernibleLinkLabel = (anchor, fallbackLabel = 'Menu link') => {
      if (!(anchor instanceof HTMLAnchorElement)) return fallbackLabel;

      const ariaLabel = (anchor.getAttribute('aria-label') || '').trim();
      if (ariaLabel) return ariaLabel;

      const text = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return text;

      const imageAlt = (anchor.querySelector('img[alt]')?.getAttribute('alt') || '').trim();
      if (imageAlt) return imageAlt;

      const titleAttr = (anchor.getAttribute('title') || '').trim();
      if (titleAttr) return titleAttr;

      return fallbackLabel;
    };

    const ensureDiscernibleLinkText = (anchor, fallbackLabel = 'Menu link') => {
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const accessibleLabel = getDiscernibleLinkLabel(anchor, fallbackLabel);
      if (accessibleLabel) {
        anchor.setAttribute('aria-label', accessibleLabel);
      }
    };

    const renderViewMoreIfNeeded = (panel, triggerElement) => {
      if (!panel) return;
      const content = panel.querySelector('[data-panel-content]');
      if (!content) return;
      const container = content.querySelector('.mega-menu3');
      if (!container) return;
      const grid = container.querySelector('.mega-menu3__grid');
      if (!grid) return;

      // 从 triggerElement 获取 view_more 数据
      let labelRaw = '';
      let linkRaw = '';
      if (triggerElement) {
        labelRaw = triggerElement.dataset.viewMoreLabel || '';
        linkRaw = triggerElement.dataset.viewMoreLink || '';
      }

      const label = labelRaw.trim() !== '' ? labelRaw.trim() : 'View More';
      const link = linkRaw.trim() !== '' ? linkRaw.trim() : null;
      // 只要配置了 link 就显示 View More，不判断数量
      const existingElements = [...grid.querySelectorAll('.mega-menu3__view-more')];
      if (!link) {
        existingElements.forEach((node) => node.remove());
        return;
      }

      let element = existingElements.shift();
      existingElements.forEach((node) => node.remove());
      if (element) {
        element.href = link;
        element.setAttribute('aria-label', label);
        const labelSpan = element.querySelector('.mega-menu3__view-more-text');
        if (labelSpan) labelSpan.textContent = label;
        grid.appendChild(element);
        return;
      }

      // 创建 View More 卡片
      element = document.createElement('a');
      element.className = 'mega-menu3__view-more';
      element.href = link;
      element.setAttribute('aria-label', label);

      const contentDiv = document.createElement('div');
      contentDiv.className = 'mega-menu3__view-more-content';

      const iconSpan = document.createElement('span');
      iconSpan.className = 'mega-menu3__view-more-icon';
      iconSpan.setAttribute('aria-hidden', 'true');
      iconSpan.innerHTML = menuSvg.viewMore;

      const labelSpan = document.createElement('span');
      labelSpan.className = 'mega-menu3__view-more-text';
      labelSpan.textContent = label;

      contentDiv.appendChild(iconSpan);
      contentDiv.appendChild(labelSpan);
      element.appendChild(contentDiv);

      // 将 View More 添加到 grid 的最后
      grid.appendChild(element);
    };

    const getDynamicPanelRefPrefix = (panel, triggerElement) => {
      if (triggerElement?.dataset?.refPrefix) return triggerElement.dataset.refPrefix;
      const panelRefPrefix = panel?.dataset?.refPrefix || '';
      return panelRefPrefix ? `${panelRefPrefix}_` : '';
    };

    const decorateDynamicPanelLinks = (panel, triggerElement) => {
      if (!panel) return;
      const content = panel.querySelector('[data-panel-content]');
      if (!content) return;
      const container = content.querySelector('.mega-menu3');
      if (!container) return;

      const refPrefix = getDynamicPanelRefPrefix(panel, triggerElement);
      const viewMoreLink = panel.querySelector('.mega-menu3__view-more[href]');
      if (viewMoreLink instanceof HTMLAnchorElement) {
        ensureDiscernibleLinkText(viewMoreLink, 'View more');
        const viewMoreLabelRaw = (viewMoreLink.textContent || '').trim();
        if (refPrefix && viewMoreLabelRaw) {
          const viewMoreLabel = viewMoreLabelRaw.replace(/\s+/g, '-');
          appendRefToAnchor(viewMoreLink, `${refPrefix}_${viewMoreLabel}`);
        }
      }

      let tertiaryIndex = 0;
      container.querySelectorAll('.mega-menu3__card').forEach((card) => {
        if (!(card instanceof HTMLElement)) return;
        tertiaryIndex += 1;
        if (!card.matches('.mega-menu3__card--is-link')) return;

        if (card instanceof HTMLAnchorElement) {
          ensureDiscernibleLinkText(card, `Menu card ${tertiaryIndex}`);
        }
        if (refPrefix) {
          appendRefToAnchor(card, `${refPrefix}_${tertiaryIndex}_img`);
        }
      });
    };

    const updatePanelContent = (panel, templateHandle, triggerElement) => {
      const target = panel.querySelector('[data-panel-content]');
      if (!target) return;
      if (panel === activePanel && panelContainer?.dataset.open === 'true') {
        freezePanelHeight();
      }
      if (templateHandle && target.dataset.renderedTemplate === templateHandle && target.querySelector('.mega-menu3')) {
        renderViewMoreIfNeeded(panel, triggerElement);
        decorateDynamicPanelLinks(panel, triggerElement);
        scheduleNaturalPanelHeight();
        return;
      }
      if (!templateHandle) {
        delete target.dataset.renderedTemplate;
        renderViewMoreIfNeeded(panel, triggerElement);
        decorateDynamicPanelLinks(panel, triggerElement);
        scheduleNaturalPanelHeight();
        return;
      }
      const template = document.querySelector(`[data-mega-menu-template="${templateHandle}"]`);
      const templateGrid =
        template?.content?.querySelector('.mega-menu3__grid') || template?.querySelector('.mega-menu3__grid');
      if (templateGrid?.dataset.variant) {
        panel.dataset.cardsVariant = templateGrid.dataset.variant;
      } else {
        delete panel.dataset.cardsVariant;
      }
      let contentLoaded = false;
      if (template && 'content' in template) {
        target.innerHTML = '';
        target.appendChild(template.content.cloneNode(true));
        target.dataset.renderedTemplate = templateHandle;
        contentLoaded = true;
      } else if (template) {
        target.innerHTML = template.innerHTML;
        target.dataset.renderedTemplate = templateHandle;
        contentLoaded = true;
      } else {
        delete target.dataset.renderedTemplate;
      }
      if (contentLoaded) {
        renderViewMoreIfNeeded(panel, triggerElement);
      }
      decorateDynamicPanelLinks(panel, triggerElement);
      scheduleNaturalPanelHeight();
    };

    navButtons.forEach((button) => {
      const panelId = button.dataset.menuTrigger;
      button.addEventListener('mouseenter', () => openPanel(panelId));
      button.addEventListener('focus', () => openPanel(panelId));
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        if (!forceOpen && expanded) {
          closeAll();
        } else {
          openPanel(panelId);
        }
      });
    });

    // 为直接跳转链接绑定事件
    directLinkButtons.forEach((button) => {
      // 直接跳转链接悬浮时关闭所有 panel
      button.addEventListener('mouseenter', closeAll);
    });

    // 活动入口链接悬浮时关闭所有 panel
    const promoLink = section.querySelector('.mega-menu__promo-link');
    if (promoLink) {
      promoLink.addEventListener('mouseenter', closeAll);
    }

    panels.forEach((panel) => {
      const subTriggers = [...panel.querySelectorAll('[data-sub-trigger]')];
      subTriggers.forEach((trigger) => {
        const subId = trigger.dataset.subTrigger;
        const handler = () => {
          if (trigger.getAttribute('aria-current') === 'true') return;
          cancelOpeningEntryCycle();
          activateSubPanel(panel, subId, trigger.dataset.subTemplate, trigger);
        };
        trigger.addEventListener('mouseenter', handler);
        trigger.addEventListener('focus', handler);
        trigger.addEventListener('click', handler);
      });
    });

    if (overlay) {
      overlay.addEventListener('click', closeAll);
    }

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') closeAll();
    });

    /* ---------------- 主题编辑器联动：选中 block 时展开对应面板 ---------------- */
    const escapeSelectorValue = (value) => {
      const stringValue = String(value);
      if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(stringValue);
      return stringValue.replace(/["\\]/g, '\\$&');
    };

    const findPanelByCategoryId = (categoryId) => {
      if (categoryId === undefined || categoryId === null || categoryId === '') return null;
      const value = String(categoryId);
      return panels.find((panel) => panel.dataset.categoryId === value || panel.dataset.directTemplate === value) || null;
    };

    // 根据 block id 打开它所属的一级菜单面板 / 二级菜单 tab
    const openPanelByBlockId = (blockId) => {
      if (!blockId) return false;
      const escapedId = escapeSelectorValue(blockId);

      // 一级菜单 block
      const navTrigger = section.querySelector(`[data-menu-trigger="${escapedId}"]`);
      if (navTrigger) {
        openPanel(navTrigger.dataset.menuTrigger);
        return true;
      }

      // 二级菜单 block
      const subTrigger = section.querySelector(`[data-sub-trigger="${escapedId}"]`);
      if (subTrigger) {
        const panel = subTrigger.closest('[data-menu-panel]');
        if (panel) {
          openPanel(panel.dataset.menuPanel);
          activateSubPanel(panel, subTrigger.dataset.subTrigger, subTrigger.dataset.subTemplate, subTrigger);
          return true;
        }
      }

      // 运营位 Banner block：打开所属一级菜单面板
      const bannerBlock = section.querySelector(`[data-mega-banner-block="${escapedId}"]`);
      if (bannerBlock) {
        const panel = findPanelByCategoryId(bannerBlock.dataset.megaBannerParent);
        if (panel) {
          openPanel(panel.dataset.menuPanel);
          return true;
        }
      }

      // mega-menu3 的 Panel card block：用 group_id 反查一级/二级菜单
      const card = document.querySelector(`[data-mega-menu-template] [data-mega-card-id="${escapedId}"]`);
      const template = card ? card.closest('[data-mega-menu-template]') : null;
      const groupId = template ? template.dataset.groupId || template.dataset.blockId || '' : '';
      if (groupId) {
        const groupSubTrigger = section.querySelector(`[data-sub-template="${escapeSelectorValue(groupId)}"]`);
        if (groupSubTrigger) {
          const panel = groupSubTrigger.closest('[data-menu-panel]');
          if (panel) {
            openPanel(panel.dataset.menuPanel);
            activateSubPanel(
              panel,
              groupSubTrigger.dataset.subTrigger,
              groupSubTrigger.dataset.subTemplate,
              groupSubTrigger
            );
            return true;
          }
        }
        const groupPanel = findPanelByCategoryId(groupId);
        if (groupPanel) {
          openPanel(groupPanel.dataset.menuPanel);
          return true;
        }
      }

      return false;
    };

    const handleEditorBlockSelect = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // 只处理本 header 内的 block，以及 mega-menu3 面板内容里的 card block
      const isInsideSection = section.contains(target);
      const isPanelCard = Boolean(target.closest('[data-mega-menu-template]'));
      if (!isInsideSection && !isPanelCard) return;
      const blockId = event.detail && event.detail.blockId;
      if (!blockId) return;
      themeEditorSelectedBlockId = blockId;
      const headerContainer = section.closest('header.shopify-section') || section;
      // 等编辑器完成自身的定位/滚动后再展开，避免打断面板高度动画
      window.requestAnimationFrame(() => {
        openPanelByBlockId(blockId);
        // 面板渲染在 header 内，确保配置时能看到它（编辑器对隐藏 block 的自动滚动无效）
        const rect = headerContainer.getBoundingClientRect();
        const isHeaderVisible = rect.bottom > 0 && rect.top < window.innerHeight;
        if (!isHeaderVisible && typeof headerContainer.scrollIntoView === 'function') {
          headerContainer.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
      });
    };

    document.addEventListener('shopify:block:select', handleEditorBlockSelect);

    // section 重新渲染后恢复选中 block 对应的面板
    if (isThemeEditor() && themeEditorSelectedBlockId) {
      window.requestAnimationFrame(() => {
        openPanelByBlockId(themeEditorSelectedBlockId);
      });
    }

    if (forceOpen && navButtons.length > 0) {
      openPanel(navButtons[0].dataset.menuTrigger);
    }

    // mega-menu3 懒加载完成后，刷新当前已打开的面板内容
    document.addEventListener(
      'mega-menu3:ready',
      function () {
        if (activePanel) {
          const shouldPlayLazyCards = canPlayOpeningEntry(activePanel);
          activateDefaultSub(activePanel);
          if (shouldPlayLazyCards) {
            playCardEntryAnimation(activePanel);
          }
        }
      },
      { once: true }
    );

    return () => {
      document.removeEventListener('shopify:block:select', handleEditorBlockSelect);
    };
  };


  const getMobileMenuRoots = () => [
    ...document.querySelectorAll('momcozy-mobile-menu, [data-mobile-mega-menu]'),
  ];

  const appendSectionResponseFragment = (target, html) => {
    if (!html || !target) return;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    const node = wrapper.firstElementChild;
    if (
      node &&
      (node.classList.contains('shopify-section') || (node.id && node.id.indexOf('shopify-section-') === 0))
    ) {
      while (node.firstChild) {
        target.appendChild(node.firstChild);
      }
    } else {
      while (wrapper.firstChild) {
        target.appendChild(wrapper.firstChild);
      }
    }
  };

  const runScripts = (parent) => {
    parent.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  };

  const setLocalizationReturnTo = () => {
    document.querySelectorAll('form[action*="localization"] input[name="return_to"]').forEach((input) => {
      const cleanPath = window.location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(\/|$)/, '/');
      input.value = cleanPath || '/';
    });
  };

  const setupDesktopLanguageLoader = (container) => {
    const placeholder = container.querySelector('[data-language-lazy-desktop]');
    if (!placeholder || placeholder.dataset.languageLoaderEnhanced === 'true') return;
    placeholder.dataset.languageLoaderEnhanced = 'true';

    let loaded = false;
    let retryCount = 0;
    const maxRetries = 3;

    if (placeholder.children.length > 0) {
      loaded = true;
      runScripts(placeholder);
      setLocalizationReturnTo();
    }

    const attemptLoad = () => {
      if (window.Shopify?.designMode) return;
      const root = window.Shopify?.routes?.root || '/';
      fetch(root + '?sections=header-language', { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((data) => {
          const html = data['header-language'];
          if (!html) return;
          appendSectionResponseFragment(placeholder, html);
          runScripts(placeholder);
          setLocalizationReturnTo();
        })
        .catch(() => {
          if (retryCount >= maxRetries) return;
          const delay = Math.pow(2, retryCount) * 1000;
          retryCount += 1;
          window.setTimeout(attemptLoad, delay);
        });
    };

    const loadPanel = () => {
      if (loaded || window.Shopify?.designMode) return;
      loaded = true;
      attemptLoad();
    };

    const header = container.closest('header, .header-section, [data-header]') || container.parentElement;
    header?.addEventListener('mouseenter', loadPanel, { once: true });
    if (window.requestIdleCallback) {
      window.requestIdleCallback(loadPanel, { timeout: 2000 });
    } else {
      window.setTimeout(loadPanel, 800);
    }
  };

  const setupMobileLanguageLoader = (container) => {
    const placeholder = container.querySelector('[data-language-lazy-mobile]');
    if (!placeholder || placeholder.dataset.languageLoaderEnhanced === 'true') return;
    placeholder.dataset.languageLoaderEnhanced = 'true';

    let loaded = false;
    let retryCount = 0;
    const maxRetries = 3;

    if (placeholder.children.length > 0) {
      loaded = true;
      runScripts(placeholder);
      setLocalizationReturnTo();
    }

    const attemptLoad = () => {
      if (window.Shopify?.designMode) return;
      const root = window.Shopify?.routes?.root || '/';
      fetch(root + '?sections=header-language-mobile', { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then((data) => {
          const html = data['header-language-mobile'];
          if (!html) return;
          appendSectionResponseFragment(placeholder, html);
          runScripts(placeholder);
          setLocalizationReturnTo();
        })
        .catch(() => {
          if (retryCount < maxRetries) {
            const delay = Math.pow(2, retryCount) * 1000;
            retryCount += 1;
            window.setTimeout(attemptLoad, delay);
          }
        });
    };

    const loadPanel = () => {
      if (loaded) return;
      if (window.Shopify?.designMode) return;
      loaded = true;
      attemptLoad();
    };

    const toggleButtons = document.querySelectorAll('[data-mobile-menu-toggle="' + container.id + '"]');
    toggleButtons.forEach((button) => {
      button.addEventListener('click', loadPanel, { once: true });
    });

    if (window.requestIdleCallback) {
      window.requestIdleCallback(loadPanel, { timeout: 3000 });
    } else {
      window.setTimeout(loadPanel, 1500);
    }
  };

  const setupMobileMenu = (container) => {
    if (!container || container.dataset.enhanced === 'true') return;
    container.dataset.enhanced = 'true';

    const parentSection = document.getElementById(container.dataset.parentContainerId || '');
    if (parentSection && !parentSection.dataset.mobileMenuOpen) {
      parentSection.dataset.mobileMenuOpen = 'false';
    }
    const navElement = parentSection ? parentSection.querySelector('.mega-menu__nav') : null;
    const toggleButtons = [...document.querySelectorAll(`[data-mobile-menu-toggle="${container.id}"]`)];
    const scrollRegion = container.querySelector('.header-mobile-menu__scroll');
    const triggers = [...container.querySelectorAll('[data-mobile-trigger]')];
    const panels = [...container.querySelectorAll('[data-mobile-panel]')];
    const focusableSelectors =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let lastFocusedToggle = null;
    const languagePanel = container.querySelector('[data-language-panel]');
    const languagePanelOverlay = container.querySelector('[data-language-panel-overlay]');
    const languagePanelOpeners = [...container.querySelectorAll('[data-language-panel-open]')];
    const languagePanelClosers = [...container.querySelectorAll('[data-language-panel-close]')];
    let lastLanguageTrigger = null;

    const filterFocusable = (elements, root = document.body) =>
      elements.filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') return false;
        if (element.closest('[hidden]')) return false;
        if (!root.contains(element)) return false;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });

    const focusLanguagePanel = () => {
      if (!languagePanel) return;
      const focusTarget =
        filterFocusable(
          [languagePanel.querySelector('[data-language-panel-close]'), languagePanel.querySelector('button')].filter(
            Boolean
          )
        ).shift() || languagePanel;
      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus({ preventScroll: true });
      }
    };

    const isLanguagePanelOpen = () => languagePanel && languagePanel.dataset.open === 'true';

    const setLanguagePanelState = (open) => {
      if (!languagePanel) return;
      languagePanel.dataset.open = open ? 'true' : 'false';
      languagePanel.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (languagePanelOverlay) {
        languagePanelOverlay.dataset.active = open ? 'true' : 'false';
        languagePanelOverlay.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
      container.classList.toggle('language-panel-open', open);
      if (open) {
        window.setTimeout(focusLanguagePanel, 30);
      } else if (lastLanguageTrigger instanceof HTMLElement) {
        lastLanguageTrigger.focus({ preventScroll: true });
      }
    };

    const appendRefToAnchor = (anchor, refValue) => {
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const originalHref = anchor.getAttribute('href');
      if (!originalHref) return;
      try {
        const url = new URL(originalHref, window.location.origin);
        url.searchParams.set('ref', refValue);
        const isAbsolute = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(originalHref);
        if (isAbsolute) {
          anchor.setAttribute('href', url.toString());
        } else {
          const relativeHref = `${url.pathname}${url.search}${url.hash}`;
          anchor.setAttribute('href', relativeHref);
        }
      } catch (error) {
        if (!originalHref.includes('javascript:') && !originalHref.startsWith('#')) {
          const separator = originalHref.includes('?') ? '&' : '?';
          anchor.setAttribute('href', `${originalHref}${separator}ref=${encodeURIComponent(refValue)}`);
        }
      }
    };

    // menu-card-group 卡片来自桌面端 mega-menu3 section 懒加载注入的 <template>，按 template_id 匹配
    const cardGroups = [...container.querySelectorAll('.menu-card-group')];

    // 进场动画：对齐 PC 的 mmpc-item-enter，两组并行（左栏 nav + 底部按钮组；右面板 header + 卡片）
    const mobileItemEntryMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const MOBILE_ITEM_ENTRY_DURATION = 600;
    const MOBILE_ITEM_STAGGER_DELAY = 60;
    const MOBILE_ITEM_STAGGER_MAX_INDEX = 7;
    const mobileEntryItemSelector =
      '.header-mobile-menu__nav-item, .header-mobile-menu__promo, .header-mobile-menu-button-group, .menu-card-group__header, .mega-menu3__card, .mega-menu3__view-more';
    let mobileEntryTimer = null;

    const clearMobileEntryAnimation = () => {
      if (mobileEntryTimer) {
        window.clearTimeout(mobileEntryTimer);
        mobileEntryTimer = null;
      }
      container.classList.remove('is-entering');
      container.querySelectorAll(mobileEntryItemSelector).forEach((item) => {
        item.style.removeProperty('--mmpc-item-delay');
      });
    };

    const staggerItems = (items, baseIndex = 0) => {
      items.forEach((item, index) => {
        const delay = Math.min(baseIndex + index, MOBILE_ITEM_STAGGER_MAX_INDEX) * MOBILE_ITEM_STAGGER_DELAY;
        item.style.setProperty('--mmpc-item-delay', `${delay}ms`);
      });
    };

    const playMobileEntryAnimation = ({ cardsOnly = false } = {}) => {
      if (mobileItemEntryMotion.matches) return;
      clearMobileEntryAnimation();

      if (!cardsOnly) {
        // 组 A：左侧 nav li（含 promo 入口）+ 底部按钮组，按钮组紧随 nav li 之后
        const navItems = [...container.querySelectorAll('.header-mobile-menu__nav-item, .header-mobile-menu__promo')];
        staggerItems(navItems);
        const buttonGroup = container.querySelector('.header-mobile-menu-button-group');
        if (buttonGroup) {
          const groupDelay = Math.min(navItems.length, MOBILE_ITEM_STAGGER_MAX_INDEX) * MOBILE_ITEM_STAGGER_DELAY;
          buttonGroup.style.setProperty('--mmpc-item-delay', `${groupDelay}ms`);
        }
        // 组 B：右面板 group header + 卡片，与组 A 同时启动
        const panelItems = [
          ...container.querySelectorAll(
            '.menu-card-group__header, .mega-menu3__card:not([hidden]), .mega-menu3__view-more'
          ),
        ];
        staggerItems(panelItems);
      } else {
        // 懒加载卡片就绪后补播，只动画新注入的卡片
        const cardItems = [...container.querySelectorAll('.mega-menu3__card:not([hidden]), .mega-menu3__view-more')];
        staggerItems(cardItems);
      }

      container.classList.add('is-entering');

      let maxDelay = 0;
      container.querySelectorAll(mobileEntryItemSelector).forEach((item) => {
        const delay = parseInt(item.style.getPropertyValue('--mmpc-item-delay'), 10) || 0;
        maxDelay = Math.max(maxDelay, delay);
      });
      mobileEntryTimer = window.setTimeout(clearMobileEntryAnimation, MOBILE_ITEM_ENTRY_DURATION + maxDelay + 50);
    };

    const decorateCardGroupLinks = (groupEl) => {
      const panelEl = groupEl.closest('[data-mobile-panel]');
      const panelId = panelEl ? panelEl.dataset.mobilePanel || '' : '';
      const triggerIndex = triggers.findIndex((trigger) => trigger.dataset.mobileTrigger === panelId);
      const primaryIndex = triggerIndex === -1 ? '' : `${triggerIndex + 1}`;
      const secondaryIndex = groupEl.dataset.secondaryIndex || '';
      let tertiaryIndex = 0;
      groupEl.querySelectorAll('.mega-menu3__card').forEach((card) => {
        tertiaryIndex += 1;
        if (!card.matches('.mega-menu3__card--is-link')) return;
        appendRefToAnchor(card, ['menu', primaryIndex, secondaryIndex, `${tertiaryIndex}`, 'img'].join('_'));
      });
    };

    const fillCardGroups = ({ finalPass = false } = {}) => {
      let injected = false;
      cardGroups.forEach((groupEl) => {
        if (groupEl.dataset.filled === 'true') return;
        const templateId = (groupEl.dataset.directTemplate || '').trim();
        if (!templateId) {
          groupEl.dataset.filled = 'true';
          groupEl.hidden = true;
          return;
        }
        const template = document.querySelector(`[data-mega-menu-template="${templateId}"]`);
        if (!template) {
          // 模板尚未注入（mega-menu3 还没加载），等 mega-menu3:ready 再重试一次
          if (finalPass) {
            groupEl.dataset.filled = 'true';
            groupEl.hidden = true;
          }
          return;
        }
        const menuNode = template.content.querySelector('.mega-menu3');
        if (!menuNode || !menuNode.querySelector('.mega-menu3__card')) {
          groupEl.dataset.filled = 'true';
          groupEl.hidden = true;
          return;
        }
        groupEl.dataset.filled = 'true';
        const gridNode = menuNode.querySelector('.mega-menu3__grid');
        if (gridNode?.dataset.variant) {
          groupEl.dataset.cardsVariant = gridNode.dataset.variant;
        }
        // 移除骨架占位 DOM，保留 menu-card-group__header
        groupEl.querySelectorAll('.menu-card-skeleton').forEach((el) => el.remove());
        groupEl.appendChild(menuNode.cloneNode(true));
        decorateCardGroupLinks(groupEl);
        injected = true;
      });
      if (injected && container.dataset.open === 'true') {
        playMobileEntryAnimation({ cardsOnly: true });
      }
    };

    if (cardGroups.length > 0) {
      fillCardGroups({ finalPass: document.querySelector('[data-mega-menu-template]') !== null });
      document.addEventListener('mega-menu3:ready', () => fillCardGroups({ finalPass: true }), { once: true });
    }

    const updateMenuOffset = () => {
      if (!parentSection) return;
      const rect = navElement ? navElement.getBoundingClientRect() : null;
      const offset = rect ? Math.max(rect.bottom, 0) : 0;
      const viewportHeight = window.innerHeight;
      container.style.top = `${offset}px`;
      container.style.height = `calc(${viewportHeight}px - ${offset}px)`;
    };

    let offsetRafId = null;
    const scheduleOffsetUpdate = () => {
      if (offsetRafId !== null) return;
      offsetRafId = window.requestAnimationFrame(() => {
        offsetRafId = null;
        updateMenuOffset();
      });
    };

    const observeLayoutChanges = () => {
      if (!parentSection) return;
      // Keep the drawer aligned when nearby layout shifts.
      const resizeTargets = new Set();
      resizeTargets.add(parentSection);
      if (navElement) resizeTargets.add(navElement);
      const sectionParent = parentSection.parentElement;
      const layoutRoot = sectionParent ? sectionParent.parentElement : null;
      const registerSiblingTargets = (scope) => {
        if (!scope) return;
        Array.from(scope.children).forEach((node) => {
          if (node instanceof Element) {
            resizeTargets.add(node);
          }
        });
      };
      registerSiblingTargets(sectionParent);
      registerSiblingTargets(layoutRoot);
      if (typeof ResizeObserver !== 'undefined' && resizeTargets.size > 0) {
        const resizeObserver = new ResizeObserver(() => scheduleOffsetUpdate());
        resizeTargets.forEach((target) => resizeObserver.observe(target));
      }
      if (typeof MutationObserver !== 'undefined') {
        const mutationObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.target === container) continue;
            scheduleOffsetUpdate();
            break;
          }
        });
        const mutationTargets = new Set();
        if (sectionParent) mutationTargets.add(sectionParent);
        if (layoutRoot) mutationTargets.add(layoutRoot);
        mutationTargets.forEach((target) =>
          mutationObserver.observe(target, {
            attributes: true,
            attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
            childList: true,
            subtree: true,
          })
        );
      }
    };
    observeLayoutChanges();

    const setToggleState = (expanded, { restoreFocus = false } = {}) => {
      toggleButtons.forEach((button) => button.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
      if (!expanded && restoreFocus && lastFocusedToggle instanceof HTMLElement) {
        lastFocusedToggle.focus({ preventScroll: true });
      }
      if (parentSection) {
        parentSection.dataset.mobileMenuOpen = expanded ? 'true' : 'false';
        parentSection.classList.toggle('mega-menu--mobile-open', expanded);
      }
    };

    setToggleState(false);
    updateMenuOffset();
    setLanguagePanelState(false);

    languagePanelOpeners.forEach((button) => {
      button.addEventListener('click', () => {
        lastLanguageTrigger = button;
        setLanguagePanelState(true);
      });
    });

    languagePanelClosers.forEach((button) => button.addEventListener('click', () => setLanguagePanelState(false)));
    if (languagePanelOverlay) {
      languagePanelOverlay.addEventListener('click', () => setLanguagePanelState(false));
    }

    const isOpen = () => {
      const layout = container.querySelector('.header-mobile-menu__layout');
      return container.dataset.open === 'true' || (layout ? layout.classList.contains('is-leaving') : false);
    };

    const focusDrawer = () => {
      const preferred = container.querySelector('.header-mobile-menu__nav-button');
      if (preferred) {
        preferred.focus({ preventScroll: true });
      } else {
        container.focus({ preventScroll: true });
      }
    };

    const getFocusable = () => filterFocusable([...container.querySelectorAll(focusableSelectors)], container);

    const openDrawer = () => {
      if (isOpen()) return;
      setLanguagePanelState(false);
      updateMenuOffset();
      if (!(lastFocusedToggle instanceof HTMLElement)) {
        lastFocusedToggle = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      }
      // 清除上次动画残留，确保进场动画从 from 帧重新播放
      const layout = container.querySelector('.header-mobile-menu__layout');
      if (layout) {
        layout.style.animation = 'none';
        void layout.offsetWidth;
        layout.style.animation = '';
      }
      container.dataset.open = 'true';
      container.setAttribute('aria-hidden', 'false');
      if (scrollRegion) {
        scrollRegion.scrollTop = 0;
      }
      document.body.classList.add('mobile-menu-open');
      // 兜底触发共享 mega-menu3 懒加载器（若已加载过，loader 内部会直接短路）
      document.dispatchEvent(new CustomEvent('momcozy:mega-menu3-request'));
      // 为兼容性，给相关父元素添加类名（替代 :has() 选择器）
      const websiteWrapper = document.querySelector('.t4s-website-wrapper');
      const headerSection = parentSection ? parentSection.closest('header.shopify-section') : null;
      if (websiteWrapper) websiteWrapper.classList.add('mobile-menu-is-open');
      if (headerSection) headerSection.classList.add('mobile-menu-is-open');
      setToggleState(true);
      playMobileEntryAnimation();
      window.setTimeout(focusDrawer, 30);
    };

    const closeDrawer = (options = {}) => {
      if (!isOpen()) return;
      setLanguagePanelState(false);
      setToggleState(false, { restoreFocus: options.returnFocus !== false });

      const layout = container.querySelector('.header-mobile-menu__layout');
      const doClose = () => {
        container.dataset.open = 'false';
        container.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('mobile-menu-open');
        // 移除父元素的类名
        const websiteWrapper = document.querySelector('.t4s-website-wrapper');
        const headerSection = parentSection ? parentSection.closest('header.shopify-section') : null;
        if (websiteWrapper) websiteWrapper.classList.remove('mobile-menu-is-open');
        if (headerSection) headerSection.classList.remove('mobile-menu-is-open');
        if (options.returnFocus !== false && lastFocusedToggle && typeof lastFocusedToggle.focus === 'function') {
          lastFocusedToggle.focus({ preventScroll: true });
        }
      };

      if (layout) {
        // 用 class 触发离场动画，与 data-open 状态完全解耦
        layout.classList.add('is-leaving');
        const onEnd = () => {
          layout.removeEventListener('animationend', onEnd);
          layout.classList.remove('is-leaving');
          doClose();
        };
        layout.addEventListener('animationend', onEnd);
      } else {
        doClose();
      }
    };

    toggleButtons.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        lastFocusedToggle = toggle;
        if (isOpen()) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    });

    const handleKeydown = (event) => {
      if (isLanguagePanelOpen()) {
        if (event.key === 'Escape') {
          event.preventDefault();
          setLanguagePanelState(false);
          return;
        }
        if (event.key === 'Tab') {
          const panelFocusable = filterFocusable(
            [...languagePanel.querySelectorAll(focusableSelectors)],
            languagePanel
          );
          if (panelFocusable.length === 0) {
            event.preventDefault();
            languagePanel.focus({ preventScroll: true });
            return;
          }
          const firstPanel = panelFocusable[0];
          const lastPanel = panelFocusable[panelFocusable.length - 1];
          if (event.shiftKey && document.activeElement === firstPanel) {
            event.preventDefault();
            lastPanel.focus();
            return;
          }
          if (!event.shiftKey && document.activeElement === lastPanel) {
            event.preventDefault();
            firstPanel.focus();
            return;
          }
          if (!panelFocusable.includes(document.activeElement)) {
            event.preventDefault();
            firstPanel.focus();
            return;
          }
        }
      }

      if (!isOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeydown);

    const desktopQuery = window.matchMedia('(min-width: 768px)');
    const handleBreakpoint = (event) => {
      if (event.matches) {
        closeDrawer({ returnFocus: false });
      }
    };
    desktopQuery.addEventListener('change', handleBreakpoint);

    window.addEventListener('resize', scheduleOffsetUpdate);
    window.addEventListener('scroll', scheduleOffsetUpdate, { passive: true });

    // Scrollspy：panels 容器本身可滚动，滚动位置驱动左侧导航高亮；点击导航则滚动到对应面板
    const scrollViewport = container.querySelector('[data-mobile-panels]');
    const navListElement = container.querySelector('.header-mobile-menu__nav-list');
    const panelById = new Map(panels.map((panel) => [panel.dataset.mobilePanel, panel]));
    const triggerById = new Map(triggers.map((trigger) => [trigger.dataset.mobileTrigger, trigger]));
    const SCROLLSPY_THRESHOLD = 16;
    let activeMobilePanelId = null;
    let programmaticScroll = false;
    let scrollResetTimer = null;
    let scrollspyRafId = null;

    // 只在自己的滚动容器内滚动，避免原生 scrollIntoView 冒泡到 window 而误触发页面滚动
    const scrollWithinContainer = (scrollParent, target, behavior) => {
      if (!scrollParent || !target) return;
      const delta = target.getBoundingClientRect().top - scrollParent.getBoundingClientRect().top;
      scrollParent.scrollBy({ top: delta, behavior });
    };

    const setActiveTrigger = (panelId) => {
      if (activeMobilePanelId === panelId) return;
      activeMobilePanelId = panelId;
      triggers.forEach((trigger) => {
        const isActive = trigger.dataset.mobileTrigger === panelId;
        trigger.classList.toggle('header-mobile-menu__nav-button--active', isActive);
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
      const activeTrigger = triggerById.get(panelId);
      if (activeTrigger) scrollWithinContainer(navListElement, activeTrigger, 'smooth');
    };

    const computeActivePanel = () => {
      if (!scrollViewport) return;
      const vTop = scrollViewport.getBoundingClientRect().top;
      let active = null;
      let bestTop = -Infinity;
      panels.forEach((panel) => {
        const top = panel.getBoundingClientRect().top - vTop;
        if (top <= SCROLLSPY_THRESHOLD && top > bestTop) {
          bestTop = top;
          active = panel.dataset.mobilePanel;
        }
      });
      if (active) setActiveTrigger(active);
    };

    const handlePanelsScroll = () => {
      if (programmaticScroll || scrollspyRafId !== null) return;
      scrollspyRafId = window.requestAnimationFrame(() => {
        scrollspyRafId = null;
        computeActivePanel();
      });
    };

    const scrollToPanel = (panelId) => {
      const panelEl = panelById.get(panelId);
      if (!panelEl || !scrollViewport) return;
      setActiveTrigger(panelId);
      programmaticScroll = true;
      if (scrollResetTimer) window.clearTimeout(scrollResetTimer);
      scrollWithinContainer(scrollViewport, panelEl, 'smooth');
      // 轮询滚动是否静止来复位 programmaticScroll，兼容不支持 scrollend 的浏览器
      const checkIdle = () => {
        const prevTop = scrollViewport.scrollTop;
        scrollResetTimer = window.setTimeout(() => {
          if (scrollViewport.scrollTop === prevTop) {
            programmaticScroll = false;
          } else {
            checkIdle();
          }
        }, 100);
      };
      scrollResetTimer = window.setTimeout(checkIdle, 100);
    };

    if (scrollViewport && triggers.length > 0) {
      scrollViewport.addEventListener('scroll', handlePanelsScroll, { passive: true });
      setActiveTrigger(triggers[0].dataset.mobileTrigger);
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => scrollToPanel(trigger.dataset.mobileTrigger));
    });

  };


  const setupMegaMenuElement = (root) => {
    if (!root || root.dataset.componentEnhanced === 'true') return;
    root.dataset.componentEnhanced = 'true';

    loaderState.config = readMegaMenuConfig(root);
    setupHeaderChrome();
    setupLogoResize(root);
    const cleanupDesktopMenu = setupDesktopMenu(root);
    // Localization is loaded by the localization-trigger Web Component.

    const headerEl = document.getElementById(loaderState.config?.headerElementId || '');
    if (headerEl) {
      headerEl.addEventListener('mouseenter', requestTemplates, { once: true });
    }

    return typeof cleanupDesktopMenu === 'function' ? cleanupDesktopMenu : null;
  };

  const setupMobileMenuElement = (root) => {
    if (!root || root.dataset.componentEnhanced === 'true') return;
    root.dataset.componentEnhanced = 'true';

    // Localization is loaded by the localization-trigger Web Component.
    setupMobileMenu(root);
  };

  const localizationState = {
    loading: null,
    panel: null,
    activeTrigger: null,
    activeForm: null,
  };

  const loadLocalizationPanel = () => {
    if (localizationState.loading) return localizationState.loading;

    const routesRoot = window.Shopify?.routes?.root || '/';
    const request = fetch(`${routesRoot}?sections=header-language`, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((sections) => sections['header-language'] || '')
      .then((html) => {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      const panel = wrapper.querySelector('localization-panel');
      if (!panel) throw new Error('Missing header-language section panel');
      const currentPanel = localizationState.panel || document.querySelector('localization-panel');
      if (currentPanel) {
        currentPanel.close?.();
        currentPanel.remove();
      }
      document.body.appendChild(panel);
      localizationState.panel = panel;
      return panel;
      })
      .catch((error) => {
        localizationState.loading = null;
        throw error;
      });

    localizationState.loading = request;
    return request;
  };

  class LocalizationTrigger extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enhanced === 'true') return;
      this.dataset.enhanced = 'true';
      this.button = this.querySelector('[data-language-panel-open]');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.open());
      const preload = () => loadLocalizationPanel().catch(() => {});
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preload, { timeout: 2000 });
      } else {
        window.setTimeout(preload, 800);
      }
    }

    open() {
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      localizationState.activeTrigger = this;
      localizationState.activeForm = this.querySelector('form[action*="localization"]');
      loadLocalizationPanel()
        .then((panel) => panel.openFor(this))
        .catch(() => {})
        .finally(() => {
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
        });
    }
  }

  class LanguagePanel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enhanced === 'true') return;
      this.dataset.enhanced = 'true';
      localizationState.panel = this;
      this.panel = this.querySelector('[data-language-panel]') || this;
      this.overlay = this.querySelector('[data-language-panel-overlay]');
      this.closeButton = this.querySelector('[data-language-panel-close]');
      this.previousBodyOverflow = '';
      this.previousScrollWidth = '';
      this.hadScrollLockClass = false;
      this.closeTimer = null;
      this.initRegionTabs();
      this.bind();
    }

    initRegionTabs() {
      const tabs = Array.from(this.querySelectorAll('[data-region-tab]'));
      const sections = Array.from(this.querySelectorAll('[data-region-section]'));
      const tabsContainer = this.querySelector('.localization-panel__region-tabs-track');
      const indicator = this.querySelector('[data-region-tab-indicator]');
      const firstTab = tabs[0];
      const firstRegion = firstTab?.dataset.regionTab;
      if (!firstRegion) return;

      const updateIndicator = (tab) => {
        if (!tabsContainer || !indicator || !tab) return;
        const tabRect = tab.getBoundingClientRect();
        const containerRect = tabsContainer.getBoundingClientRect();
        const position = tabRect.left - containerRect.left + tabsContainer.scrollLeft;
        indicator.style.setProperty('--tabs-indicator-width', `${tab.offsetWidth}px`);
        indicator.style.setProperty('--tabs-indicator-position', `${position}px`);
      };

      const centerTab = (tab) => {
        if (!tabsContainer || !tab || tabsContainer.scrollWidth <= tabsContainer.clientWidth) return;
        const maxScrollLeft = tabsContainer.scrollWidth - tabsContainer.clientWidth;
        const tabRect = tab.getBoundingClientRect();
        const containerRect = tabsContainer.getBoundingClientRect();
        const tabPosition = tabRect.left - containerRect.left + tabsContainer.scrollLeft;
        const targetScrollLeft = tabPosition - (tabsContainer.clientWidth - tabRect.width) / 2;
        tabsContainer.scrollTo({
          left: Math.min(maxScrollLeft, Math.max(0, targetScrollLeft)),
          behavior: 'smooth',
        });
      };

      tabs.forEach((tab) => {
        tab.setAttribute('aria-selected', tab === firstTab ? 'true' : 'false');
      });
      sections.forEach((section) => {
        section.hidden = section.dataset.regionSection !== firstRegion;
      });
      updateIndicator(firstTab);

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const region = tab.dataset.regionTab;
          tabs.forEach((candidate) => {
            candidate.setAttribute('aria-selected', candidate === tab ? 'true' : 'false');
          });
          sections.forEach((section) => {
            section.hidden = section.dataset.regionSection !== region;
          });
          updateIndicator(tab);
          centerTab(tab);
        });
      });

      window.addEventListener('resize', () => {
        updateIndicator(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true'));
      }, { passive: true });
    }

    openFor(trigger) {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      this.activeTrigger = trigger;
      this.panel.dataset.closing = 'false';
      this.panel.dataset.open = 'true';
      this.panel.setAttribute('aria-hidden', 'false');
      this.overlay?.setAttribute('aria-hidden', 'false');
      if (this.overlay) {
        this.overlay.dataset.closing = 'false';
        this.overlay.dataset.active = 'true';
      }
      this.previousBodyOverflow = document.body.style.overflow;
      this.previousScrollWidth = document.documentElement.style.getPropertyValue('--scroll-w').trim();
      this.hadScrollLockClass = document.documentElement.classList.contains('localization-scroll-lock');
      if (!this.previousScrollWidth) {
        const scrollWidth = Math.max(window.innerWidth - document.documentElement.clientWidth, 0);
        document.documentElement.style.setProperty('--scroll-w', `${scrollWidth}px`);
      }
      document.documentElement.classList.add('localization-scroll-lock');
      document.body.style.overflow = 'hidden';
      trigger?.button?.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => this.closeButton?.focus({ preventScroll: true }), 20);
    }

    close() {
      if (this.panel.dataset.open !== 'true') return;
      this.panel.dataset.open = 'false';
      this.panel.dataset.closing = 'true';
      this.panel.setAttribute('aria-hidden', 'true');
      this.overlay?.setAttribute('aria-hidden', 'true');
      if (this.overlay) {
        this.overlay.dataset.active = 'false';
        this.overlay.dataset.closing = 'true';
      }
      document.body.style.overflow = this.previousBodyOverflow;
      this.previousBodyOverflow = '';
      if (this.previousScrollWidth) {
        document.documentElement.style.setProperty('--scroll-w', this.previousScrollWidth);
      } else {
        document.documentElement.style.removeProperty('--scroll-w');
      }
      if (!this.hadScrollLockClass) document.documentElement.classList.remove('localization-scroll-lock');
      this.previousScrollWidth = '';
      this.hadScrollLockClass = false;
      this.activeTrigger?.button?.setAttribute('aria-expanded', 'false');
      this.activeTrigger?.button?.focus({ preventScroll: true });
      this.closeTimer = window.setTimeout(() => {
        this.panel.dataset.closing = 'false';
        if (this.overlay) this.overlay.dataset.closing = 'false';
        this.closeTimer = null;
      }, 300);
    }

    bind() {
      this.closeButton?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      this.panel.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const item = target?.closest('.localization-panel__item');
        if (!item || !this.panel.contains(item)) return;

        const anchor = target.closest('a');
        if (anchor) event.preventDefault();

        const domain = (item.dataset.domain || '').trim();
        document.dispatchEvent(
          new CustomEvent('momcozy:site-switched', {
            detail: {
              country: (item.dataset.country || '').toUpperCase(),
              language: (item.dataset.language || '').toLowerCase(),
              currency: item.dataset.currency || '',
              domain,
              source: 'localization-panel',
            },
          })
        );
        this.close();
        if (domain) {
          window.location.assign(domain);
          return;
        }

        const form = localizationState.activeForm;
        const countryInput = form?.querySelector('input[name="country_code"]');
        const languageInput = form?.querySelector('input[name="language_code"]');
        if (countryInput) countryInput.value = item.dataset.country || '';
        if (languageInput) languageInput.value = (item.dataset.language || '').toLowerCase();
        if (item.dataset.currency) {
          try {
            window.localStorage.setItem('T4Currency', item.dataset.currency);
          } catch (error) {
            console.warn('Unable to persist currency selection', error);
          }
        }
        form?.submit();
      });
      this.panel.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const item = event.target instanceof Element ? event.target.closest('.localization-panel__item') : null;
        if (!item || !this.panel.contains(item)) return;
        event.preventDefault();
        item.click();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
      });
    }
  }

  class MomcozyMegaMenu extends HTMLElement {
    connectedCallback() {
      this.cleanupMegaMenu = setupMegaMenuElement(this) || null;
    }

    disconnectedCallback() {
      if (typeof this.cleanupMegaMenu === 'function') {
        this.cleanupMegaMenu();
        this.cleanupMegaMenu = null;
      }
    }
  }

  class MomcozyMobileMenu extends HTMLElement {
    connectedCallback() {
      setupMobileMenuElement(this);
    }

    disconnectedCallback() {}
  }

  const defineElements = () => {
    if (!customElements.get('momcozy-mega-menu')) {
      customElements.define('momcozy-mega-menu', MomcozyMegaMenu);
    }
    if (!customElements.get('momcozy-mobile-menu')) {
      customElements.define('momcozy-mobile-menu', MomcozyMobileMenu);
    }
    if (!customElements.get('localization-trigger')) {
      customElements.define('localization-trigger', LocalizationTrigger);
    }
    if (!customElements.get('localization-panel')) {
      customElements.define('localization-panel', LanguagePanel);
    }
  };

  const setupLoader = () => {
    defineElements();

    getMenuRoots().forEach((root) => setupMegaMenuElement(root));
    getMobileMenuRoots().forEach((root) => setupMobileMenuElement(root));

    document.addEventListener('momcozy:mega-menu3-request', requestTemplates);
    scheduleIdleLoad();
  };

  globalScope.loader = {
    request: requestTemplates,
    setup: setupLoader,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupLoader, { once: true });
  } else {
    setupLoader();
  }
})();
